import type { PlatformPlan } from '@/shared/api/resources';

/** Nome do plano como a tela mostra. A chave vem do servidor. */
export const planos: Record<PlatformPlan, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

export const opcoesDePlano = (Object.keys(planos) as PlatformPlan[]).map((plano) => ({
  value: plano,
  label: planos[plano],
}));

/**
 * Acoes da trilha da plataforma.
 *
 * As consultas tambem ficam na trilha: quem administra enxerga todas as empresas, e
 * enxergar sem registro e o que a plataforma recusa. A tela separa as duas para que
 * as alteracoes nao se percam no meio das leituras.
 */
export const acoes: Record<string, string> = {
  'plataforma.empresa.list': 'Consultou a lista de empresas',
  'plataforma.empresa.view': 'Consultou a empresa',
  'plataforma.empresa.create': 'Criou a empresa',
  'plataforma.empresa.plan': 'Mudou o plano',
  'plataforma.empresa.suspend': 'Suspendeu a empresa',
  'plataforma.empresa.reactivate': 'Reativou a empresa',
  'plataforma.funcionalidade.update': 'Alterou uma funcionalidade',
  'plataforma.catalogo.view': 'Consultou o catalogo',
  'plataforma.trilha.view': 'Consultou a trilha',
  'plataforma.administrador.grant': 'Concedeu a administracao da plataforma',
  'plataforma.administrador.revoke': 'Revogou a administracao da plataforma',
  'plataforma.backup.create': 'Pediu um backup',
  'plataforma.backup.finish': 'Backup terminou',
  'plataforma.backup.download': 'Baixou um backup',
  'plataforma.backup.drive.send': 'Pediu o envio de um backup ao Google Drive',
  'plataforma.backup.drive.upload': 'Envio ao Google Drive terminou',
  'plataforma.backup.google.connect': 'Conectou a conta do Google Drive',
  'plataforma.backup.google.connect_fail': 'Conexao com o Google Drive nao concluida',
  'plataforma.backup.google.update': 'Ajustou o Google Drive',
  'plataforma.backup.google.disconnect': 'Desconectou a conta do Google Drive',
  'plataforma.governanca.view': 'Consultou as regras e a governanca',
  'plataforma.governanca.update': 'Alterou as regras e a governanca',
  // Configuracao de plataforma da empresa (P-PLAT; R5-B29).
  'plataforma.configuracao.view': 'Consultou a configuracao de plataforma da empresa',
  'plataforma.configuracao.update': 'Alterou a configuracao de plataforma da empresa',
  'plataforma.manutencao.view': 'Consultou as janelas de manutencao',
  'plataforma.manutencao.create': 'Abriu uma janela de manutencao',
  'plataforma.manutencao.update': 'Alterou uma janela de manutencao',
  'plataforma.manutencao.end': 'Encerrou a manutencao',
  'plataforma.manutencao.cancel': 'Cancelou uma janela de manutencao',
};

/**
 * Como a tela de regras e governanca apresenta cada permissao governada.
 *
 * O texto diz o que a permissao libera em termos de negocio. A chave tecnica
 * aparece junto, porque e ela que a matriz de papeis da empresa mostra.
 */
export const permissoesGovernadas: Record<
  'corretores.dados_bancarios.update' | 'tomadores.credito.update',
  { titulo: string; descricao: string }
> = {
  'corretores.dados_bancarios.update': {
    titulo: 'Quem altera a conta bancaria e o Pix da corretora',
    descricao:
      'Conta e Pix sao o destino do repasse da comissao. Por padrao, so a administracao da empresa altera.',
  },
  'tomadores.credito.update': {
    titulo: 'Quem altera o limite de credito e a situacao do tomador',
    descricao:
      'Inclui bloquear, desbloquear, ativar e inativar o tomador. Por padrao, so a administracao da empresa altera.',
  },
};

/**
 * Ator sem sessao: a linha de comando, ou a rotina de envio automatico dos backups.
 * Os dois aparecem sem identidade, e o rotulo diz qual foi.
 */
export function origemSemSessao(rotulo: string): string {
  return rotulo.startsWith('rotina') ? 'rotina do servidor' : 'linha de comando';
}

export function rotuloDaAcao(acao: string): string {
  return acoes[acao] ?? acao;
}

/**
 * Converte a data escolhida no fim daquele dia, no fuso de quem escolheu.
 *
 * Quem marca "vale ate 30/11" espera que o dia 30 inteiro conte. Gravar a meia-noite
 * do comeco do dia tiraria um dia da validade sem ninguem perceber.
 */
export function fimDoDia(data: string): string {
  return new Date(`${data}T23:59:59`).toISOString();
}

/** A data no formato do campo de data, a partir do instante gravado. */
export function paraCampoDeData(instante: string | null): string {
  if (!instante) return '';
  const data = new Date(instante);
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}
